import { useState, useEffect, useCallback, useRef } from 'react';
import { IconSearchFilled } from '@tabler/icons-react';
import Busqueda from '../components/Busqueda';
import TablaInventario from '../components/TablaInventario';
import DeleteIconCircle from '../components/DeleteIconCircle';
import EditarAzulButton from '../Utils/EditarAzulButton';
import StockBadge from '../Utils/StockBadge';
import { ensureMinLoading } from '../Utils/withLoading';
import ModalAgregarItemInventario from '../components/ModalAgregarItemInventario';
import ModalEditarItemInventario from '../components/ModalEditarItemInventario';
import ModalEliminarItemInventario from '../components/ModalEliminarItemInventario';
import AgujeroNegro from '../components/AgujeroNegro';
import { apiFetch } from '../api/client';
import TipBlue from '../Utils/TipBlue';

const FONT = '"Public Sans", sans-serif';
const POLL_INTERVAL_MS = 10000;

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function TablaSkeleton() {
  return (
    <div className="flex flex-col gap-1.5" role="status" aria-label="Cargando insumos">
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <div
          key={i}
          className="flex items-center gap-4 rounded-2xl animate-pulse"
          style={{ height: '64px', padding: '0 12px' }}
        >
          <span
            className="shrink-0 rounded-full"
            style={{ width: '22px', height: '22px', backgroundColor: '#141414' }}
          />
          <span
            className="rounded-full"
            style={{ width: '28%', height: '12px', backgroundColor: '#141414' }}
          />
          <span
            className="rounded-full"
            style={{ width: '14%', height: '12px', backgroundColor: '#141414' }}
          />
          <span
            className="ml-auto rounded-full"
            style={{ width: '11%', height: '12px', backgroundColor: '#141414' }}
          />
        </div>
      ))}
    </div>
  );
}

function SinResultados({ query, onClear }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <span
        className="flex items-center justify-center w-11 h-11 rounded-2xl"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.035)' }}
      >
        <IconSearchFilled size={21} color="rgba(255, 255, 255, 0.3)" />
      </span>
      <div>
        <p
          className="m-0"
          style={{
            fontSize: '15px',
            lineHeight: '20px',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'rgba(255, 255, 255, 0.9)',
            fontFamily: FONT,
          }}
        >
          Sin resultados
        </p>
        <p
          className="m-0 mt-1"
          style={{
            fontSize: '13px',
            lineHeight: '19px',
            fontWeight: 500,
            color: 'rgba(255, 255, 255, 0.4)',
            fontFamily: FONT,
          }}
        >
          No encontramos insumos para «{query}»
        </p>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="flex items-center justify-center h-10 px-4 mt-1 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.97] [@media(hover:hover)]:hover:bg-white/[0.1]"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'rgba(255, 255, 255, 0.88)',
          fontFamily: FONT,
        }}
      >
        Limpiar búsqueda
      </button>
    </div>
  );
}

export default function InventarioItems({ notify }) {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [search, setSearch] = useState('');
  const tableRef = useRef(null);

  const fetchInventory = useCallback(async () => {
    try {
      const res = await apiFetch(`/inventory`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setInventory(data);
    } catch (e) {
      console.error('Error al cargar inventario:', e);
    }
  }, []);

  useEffect(() => {
    let stopped = false;
    let timer = null;
    let controller = null;
    let firstRun = true;

    const load = async (showSpinner) => {
      controller = new AbortController();
      if (showSpinner) setLoading(true);
      try {
        if (document.hidden) return;
        const res = await apiFetch(`/inventory`, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!stopped) setInventory(data);
      } catch {
        // silence network errors; keep last known data
      } finally {
        if (!stopped && showSpinner) setLoading(false);
      }
    };

    const loop = async () => {
      if (stopped) return;
      await load(firstRun);
      firstRun = false;
      if (stopped) return;
      timer = setTimeout(loop, POLL_INTERVAL_MS);
    };

    loop();

    const handleVisibility = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      if (document.hidden || stopped) return;
      load(false);
      timer = setTimeout(loop, POLL_INTERVAL_MS);
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      if (controller) controller.abort();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const handleEdit = (row) => {
    setSelectedItem(row);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setDeleteModalOpen(true);
  };

  const handleBulkDelete = async (ids) => {
    if (!ids || ids.length === 0) return;
    const startedAt = Date.now();
    notify?.('loading', `Eliminando ${ids.length} ${ids.length === 1 ? 'insumo' : 'insumos'}...`);
    let failed = 0;
    for (const id of ids) {
      try {
        const res = await apiFetch(`/inventory/${id}`, { method: 'DELETE' });
        if (!res.ok) failed++;
      } catch {
        failed++;
      }
    }
    await ensureMinLoading(startedAt);
    notify?.('clear');
    fetchInventory();
    if (failed === 0) {
      notify?.('success', `${ids.length} ${ids.length === 1 ? 'insumo eliminado' : 'insumos eliminados'} correctamente`);
    } else if (failed < ids.length) {
      notify?.('error', `${failed} ${failed === 1 ? 'insumo no pudo' : 'insumos no pudieron'} ser eliminados`);
    } else {
      notify?.('error', 'No se pudieron eliminar los insumos. Si el problema persiste, reporta el error.');
    }
  };

  const handleSingleDelete = async () => {
    if (!itemToDelete) return;
    const startedAt = Date.now();
    notify?.('loading', 'Eliminando insumo...');
    let ok = false;
    try {
      const res = await apiFetch(`/inventory/${itemToDelete.id}`, { method: 'DELETE' });
      ok = res.ok;
    } catch (e) {
      ok = false;
    }
    await ensureMinLoading(startedAt);
    notify?.('clear');
    if (ok) {
      fetchInventory();
      notify?.('success', `"${itemToDelete.name}" eliminado correctamente`);
      setDeleteModalOpen(false);
      setItemToDelete(null);
    } else {
      notify?.('error', 'No se pudo eliminar el insumo. Si el problema persiste, reporta el error.');
    }
  };

  const normalizedQuery = normalizeText(search.trim());
  const filteredInventory = normalizedQuery
    ? inventory.filter((item) =>
        [item.name, item.sku, item.supplier].some((field) => normalizeText(field).includes(normalizedQuery))
      )
    : inventory;

  const countLabel = normalizedQuery
    ? `${filteredInventory.length} ${filteredInventory.length === 1 ? 'resultado' : 'resultados'}`
    : `${inventory.length} ${inventory.length === 1 ? 'insumo' : 'insumos'}`;

  const columns = [
    { key: 'name', label: 'Nombre', flex: 2, minWidth: '160px' },
    { key: 'unit_of_measure', label: 'Unidad', flex: 0.7, minWidth: '80px', badge: true },
    { key: 'current_quantity', label: 'Cantidad', flex: 0.8, minWidth: '80px' },
    { key: 'minimum_stock', label: 'Mínimo', flex: 0.7, minWidth: '80px' },
    { key: 'maximum_stock', label: 'Máximo', flex: 0.7, minWidth: '80px' },
    { key: 'supplier', label: 'Proveedor', flex: 1.2, minWidth: '140px' },
    {
      key: 'stock_status',
      label: 'Estado',
      flex: 0.8,
      minWidth: '90px',
      render: (row) => <StockBadge current={row.current_quantity} minimum={row.minimum_stock} />,
    },
    {
      key: 'editar',
      label: '',
      flex: 0,
      minWidth: '100px',
      render: (row) => <EditarAzulButton size="lg" onClick={() => handleEdit(row)} />,
    },
    {
      key: 'eliminar',
      label: '',
      flex: 0,
      minWidth: '50px',
      render: (row) => <DeleteIconCircle solid size={44} onClick={() => handleDeleteClick(row)} />,
    },
  ];

  return (
    <div className="flex flex-col h-screen bg-black overflow-hidden">
      <header className="shrink-0 px-4 lg:px-6 pt-5 pb-4 border-b border-white/[0.16]">
        <div className="min-w-0">
          <h1
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            Inventario
          </h1>
          <p
            className="m-0 mt-1"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
              fontFamily: FONT,
            }}
          >
            Control de insumos y existencias
          </p>
        </div>
      </header>

      <div className="flex flex-col flex-1 min-h-0 px-4 lg:px-6 pt-5 pb-4">
        <div className="flex items-center gap-3 shrink-0 pb-4">
          <div className="flex-1 max-w-[560px]">
            <Busqueda
              size="lg"
              value={search}
              onChange={setSearch}
              placeholder="Buscar insumo, SKU o proveedor"
            />
          </div>
          <span
            className="ml-auto shrink-0 hidden sm:inline"
            style={{
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.35)',
              fontFamily: FONT,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {loading ? 'Cargando…' : countLabel}
          </span>
        </div>

        <div className="flex-1 min-h-0" aria-busy={loading}>
          {loading ? (
            <div className="h-full overflow-y-auto pedidos-scroll">
              <TablaSkeleton />
            </div>
          ) : (
            <TablaInventario
              ref={tableRef}
              columns={columns}
              rows={filteredInventory}
              selectable
              onDelete={handleBulkDelete}
              emptyComponent={
                normalizedQuery ? (
                  <SinResultados query={search.trim()} onClear={() => setSearch('')} />
                ) : (
                  <AgujeroNegro message="Tu inventario está vacío. Agrega insumos antes de que se vuelva un caos espacial." />
                )
              }
            />
          )}
        </div>
      </div>

      <div className="shrink-0 px-4 lg:px-6 py-4 border-t border-white/[0.16] flex items-center justify-between gap-4">
        <TipBlue>Usa Agregar para registrar nuevos insumos y el lápiz azul para editarlos.</TipBlue>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="flex items-center justify-center h-12 px-8 rounded-full border-none cursor-pointer touch-manipulation select-none font-semibold transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-[#0077ed]"
          style={{
            backgroundColor: '#0071e3',
            color: '#fff',
            fontSize: '15px',
            letterSpacing: '-0.01em',
            fontFamily: FONT,
          }}
        >
          Agregar
        </button>
      </div>

      <ModalAgregarItemInventario
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => { fetchInventory(); notify?.('success', 'Insumo agregado correctamente'); }}
        notify={notify}
      />

      <ModalEditarItemInventario
        isOpen={editModalOpen}
        onClose={() => { setEditModalOpen(false); setSelectedItem(null); }}
        onSaved={() => { fetchInventory(); notify?.('success', 'Insumo actualizado correctamente'); }}
        item={selectedItem}
        notify={notify}
      />

      <ModalEliminarItemInventario
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setItemToDelete(null); }}
        onConfirm={handleSingleDelete}
        itemName={itemToDelete?.name}
      />
    </div>
  );
}
